import { Collapse, type CollapseProps } from 'antd';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { COLOR_OPTIONS } from '@protector/styles';
import { ExpandArrow } from '../expand-arrow';
import { STTypo, STTypoModels } from '../typo';
import styles from './group-collapse.module.css';

const collapseSlotClassNames: CollapseProps['classNames'] = {
  root: styles.collapseRoot,
  header: styles.collapseHeader,
  body: styles.collapseBody
};

const renderExpandIcon: CollapseProps['expandIcon'] = ({ isActive }) => (
  <ExpandArrow expanded={!!isActive} className={styles.collapseArrow} />
);

export type GroupCollapseVariant = 'default' | 'neutral';

export interface GroupCollapseItem {
  key: string;
  title: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  arrow?: 'start' | 'inline' | 'end';
  headerTestId?: string;
  children: ReactNode;
}

interface GroupCollapseProps {
  items: GroupCollapseItem[];
  dataTestId?: string;
  defaultActiveKeys?: string[];
  destroyOnHidden?: boolean;
  activeKeys?: string[];
  onActiveKeysChange?: (activeKeys: string[]) => void;
  variant?: GroupCollapseVariant;
}

export const GroupCollapse = ({
  items,
  dataTestId,
  defaultActiveKeys,
  destroyOnHidden,
  activeKeys,
  onActiveKeysChange,
  variant = 'default'
}: GroupCollapseProps) => {
  const groupKeys = useMemo(() => items.map(item => item.key), [items]);
  const seenKeysRef = useRef<Set<string>>(new Set(groupKeys));
  const [internalActiveKeys, setInternalActiveKeys] = useState<string[]>(defaultActiveKeys ?? groupKeys);
  const isControlled = activeKeys !== undefined;
  const currentActiveKeys = isControlled ? activeKeys : internalActiveKeys;

  useEffect(() => {
    if (isControlled) {
      return;
    }
    const newKeys = groupKeys.filter(key => !seenKeysRef.current.has(key));
    if (newKeys.length === 0) {
      return;
    }
    newKeys.forEach(key => seenKeysRef.current.add(key));
    setInternalActiveKeys(previousKeys => [...previousKeys, ...newKeys]);
  }, [groupKeys, isControlled]);

  const handleActiveKeysChange: CollapseProps['onChange'] = keys => {
    const nextKeys = (Array.isArray(keys) ? keys : [keys]).map(String);
    if (isControlled) {
      onActiveKeysChange?.(nextKeys);
    } else {
      setInternalActiveKeys(nextKeys);
    }
  };

  const variantClass = variant === 'neutral' ? styles.collapseRootNeutral : undefined;

  const collapseItems: CollapseProps['items'] = items.map(item => {
    const isActive = currentActiveKeys.includes(item.key);

    return {
      key: item.key,
      showArrow: item.arrow !== 'inline' && item.arrow !== 'end',
      className: classNames(styles.collapseItem, styles.collapseItemCard),
      label: (
        <div className={styles.groupHeader} data-testid={item.headerTestId}>
          <div className={styles.groupHeaderMain}>
            {item.leading}
            <GroupCollapseTitle variant={variant}>{item.title}</GroupCollapseTitle>
            {item.arrow === 'inline' && <ExpandArrow expanded={isActive} className={styles.collapseArrow} />}
          </div>
          {item.arrow === 'end' && <ExpandArrow expanded={isActive} className={styles.collapseArrow} />}
          {item.trailing}
        </div>
      ),
      children: item.children
    };
  });

  return (
    <Collapse
      bordered={false}
      data-testid={dataTestId}
      activeKey={currentActiveKeys}
      onChange={handleActiveKeysChange}
      expandIcon={renderExpandIcon}
      classNames={collapseSlotClassNames}
      destroyOnHidden={destroyOnHidden}
      items={collapseItems}
      className={variantClass}
    />
  );
};

const GroupCollapseTitle = ({ children, variant }: { children: ReactNode; variant: GroupCollapseVariant }) => (
  <STTypo
    type={STTypoModels.TypeOptions.H5}
    fontFamily='Noto Sans'
    fontWeight={600}
    lineHeight='24px'
    color={variant === 'neutral' ? COLOR_OPTIONS.NEUTRAL_100 : COLOR_OPTIONS.NEUTRAL_00}>
    {children}
  </STTypo>
);
